import { ref, watch, computed } from 'vue'
import {
  type TestEvents,
  type TestEvent,
  DEFAULT_TEST_EVENTS,
} from '@/components/testEventsModels'
import { api } from '@/api/client'
import { useAuth } from '@/composables/useAuth'

const testEvents = ref<TestEvents>(loadTestEvents())
const isSyncing = ref<boolean>(false)

function loadTestEvents(): TestEvents {
  const stored = localStorage.getItem('testEvents')
  if (stored) {
    try {
      return JSON.parse(stored)
    } catch (e) {
      console.error('Fehler beim Laden der TestEvents:', e)
    }
  }
  return JSON.parse(JSON.stringify(DEFAULT_TEST_EVENTS))
}

function saveTestEvents(testEvents: TestEvents) {
  localStorage.setItem('testEvents', JSON.stringify(testEvents))
}

watch(
  testEvents,
  (newTestEvents) => {
    saveTestEvents(newTestEvents)
  },
  { deep: true },
)

export function useTestEvents() {
  const { user } = useAuth()

  const syncPendingEvents = async () => {
    if (isSyncing.value) return
    if (typeof navigator !== 'undefined' && !navigator.onLine) return
    if (!user.verified || !user.email || !user.verificationCode) return

    const pendingEvents = testEvents.value.events.filter((e) => !e.synchronized)
    if (pendingEvents.length === 0) return

    isSyncing.value = true
    try {
      const payloadEvents = pendingEvents.map((e) => {
        let median = 0
        let failures = 0
        if ('median' in e && typeof (e as { median?: number }).median === 'number') {
          median = (e as { median: number }).median
        }
        if ('failures' in e && typeof (e as { failures?: number }).failures === 'number') {
          failures = (e as { failures: number }).failures
        }
        return {
          type: e.type,
          date: e.date instanceof Date ? e.date.toISOString() : new Date(e.date).toISOString(),
          median,
          failures,
        }
      })

      const { data, error } = await api.POST('/api/sync-test-events', {
        body: {
          email: user.email,
          verificationCode: user.verificationCode,
          events: payloadEvents,
        },
      })

      if (!error && data?.success) {
        pendingEvents.forEach((e) => {
          e.synchronized = true
        })
        saveTestEvents(testEvents.value)
      }
    } catch (e) {
      console.error('Fehler bei der Synchronisation der TestEvents:', e)
    } finally {
      isSyncing.value = false
    }
  }

  const addTestEvent = (testEvent: TestEvent) => {
    testEvents.value.events.push(testEvent)
    void syncPendingEvents()
  }

  const pendingEventsCount = computed(
    () => testEvents.value.events.filter((e) => !e.synchronized).length
  )

  return {
    testEvents,
    pendingEventsCount,
    isSyncing,
    addTestEvent,
    syncPendingEvents,
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    const { syncPendingEvents } = useTestEvents()
    void syncPendingEvents()
  })
}
